/**
 * Pure derivations behind the My Book dashboard (`client/src/pages/MyBook.tsx`).
 *
 * The `GET /api/my-book` payload deliberately ships raw project rows and lets
 * the client count — see the frozen contract in
 * `docs/specs/2026-09-10-my-book-dashboard-spec.md`. Everything the cards and
 * the summary strip display is therefore computed here, so the numbers are
 * testable without a server and can never drift between the strip and a card
 * (both read the same functions).
 *
 * Input types are structural on purpose: `MyBookAccount`/`MyBookProject` from
 * `api.ts` satisfy them, but this module stays free of any api.ts dependency
 * (same shape as `sectionAutoCollapse.ts`).
 */

import { isCurrentAssignee } from "./currentAssignee";

const DAY_MS = 24 * 60 * 60 * 1000;

// Since v1.5.64 these three are FALLBACK defaults: the firm-tuned values arrive on both
// dashboard payloads as `display_thresholds` (my_accounts_settings, admin gear on
// /my-accounts), and these constants apply only when that field is absent (an old cached
// payload, or a response predating the feature). The server pins the same literals from its
// side in server/lib/myAccountsSettings.test.ts.

/** A relationship this quiet gets an amber "going quiet" pill instead of plain text. */
export const GOING_QUIET_DAYS = 14;
/** A waiting-on-client item older than this is called out in amber. */
export const WAITING_ON_CLIENT_AGING_DAYS = 7;
/** Idle days at/after which a stall reads rose rather than amber. */
export const STALL_SEVERE_DAYS = 14;
/** Oldest-unread age at/after which the shared-inbox bubble reads amber (v1.5.65). */
export const INBOX_UNREAD_AMBER_DAYS = 3;
/** Oldest-unread age at/after which the shared-inbox bubble reads red (v1.5.65). */
export const INBOX_UNREAD_RED_DAYS = 6;

export interface DerivableProject {
  overdue?: boolean | null;
  stalled?: boolean | null;
  days_idle?: number | null;
  due_date?: string | null;
  current_assignee_id?: string | null;
  current_assignee_ids?: string[] | null;
}

export interface DerivableAccount {
  projects?: DerivableProject[] | null;
  relationship?: {
    waiting_on_client?: { count?: number | null } | null;
  } | null;
}

export interface AccountDerived {
  /** Every project in the payload is already an open status, so this is the list length. */
  openCount: number;
  overdueCount: number;
  stalledCount: number;
  /** Projects whose current step is held by the signed-in user. */
  reviewsForMe: number;
  /**
   * Largest `days_idle` among STALLED projects — the number the "Stalled · Nd idle"
   * pill shows. `null` means "don't claim a day count": either nothing is stalled
   * (gate on `stalledCount` first) or a stalled project has no recorded activity
   * at all, which the contract represents as a null `days_idle`.
   */
  worstStalledDaysIdle: number | null;
}

export interface BookTotals {
  stalled: number;
  overdue: number;
  reviewsForMe: number;
  waitingOnClient: number;
}

/**
 * Whole days between `iso` and now. Clamped at 0 so a clock skew or a
 * future-dated follow-up can never render as "-3d ago". Null for anything we
 * cannot parse, so callers render a placeholder instead of "NaNd ago".
 *
 * A bare YYYY-MM-DD (the `through_date` / `follow_up_at` shape) is a CALENDAR
 * date, so it gets a calendar-day difference: pin it to local noon (the repo's
 * standing date gotcha — parsed as written it is UTC midnight, i.e. the previous
 * evening in ET), compare against TODAY's local noon, and round. Noon-to-noon
 * local differences are whole days in any timezone including across DST, so
 * "Nd behind" never over- or under-states by one depending on the hour of day.
 * Timestamps keep elapsed-time semantics (floored full days).
 */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const then = new Date(`${iso}T12:00:00`).getTime();
    if (Number.isNaN(then)) return null;
    const todayNoon = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).getTime();
    const days = Math.round((todayNoon - then) / DAY_MS);
    return days <= 0 ? 0 : days;
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diff = now.getTime() - then;
  if (diff <= 0) return 0;
  return Math.floor(diff / DAY_MS);
}

/** "2d ago" / "today" / "" (nothing renderable) — the card's relative-time wording. */
export function relativeDays(iso: string | null | undefined, now: Date = new Date()): string {
  const days = daysSince(iso, now);
  if (days === null) return "";
  if (days === 0) return "today";
  return `${days}d ago`;
}

/**
 * Colour band for a stall pill. Unknown idle (no activity ever recorded) is the
 * worst case, not the mildest — a project nobody has ever touched must not read
 * as gently amber.
 *
 * `severeDays` is the firm-tuned cut from the payload's `display_thresholds`
 * (v1.5.64); the shipped constant is the fallback when the payload predates it.
 */
export function stalledSeverity(
  daysIdle: number | null | undefined,
  severeDays: number = STALL_SEVERE_DAYS,
): "amber" | "red" {
  if (daysIdle === null || daysIdle === undefined) return "red";
  return daysIdle >= severeDays ? "red" : "amber";
}

/**
 * Colour band for the shared-inbox unread bubble (v1.5.65), keyed on the oldest
 * unread email's age. Red is checked FIRST so an inverted pair (amber cut saved
 * past the red cut — allowed with a warning, like the stall inversion) collapses
 * the amber band rather than letting old mail render green.
 *
 * Unknown age → green, the OPPOSITE default from stalledSeverity: an untouched
 * project is a problem, but an unread count whose date failed to parse is a data
 * glitch — a wrong green is recoverable, a wrong red cries wolf. (Unreachable
 * from the migration-112 SQL, where min() runs over the same rows as count().)
 */
export function unreadAgeSeverity(
  oldestDays: number | null | undefined,
  amberDays: number = INBOX_UNREAD_AMBER_DAYS,
  redDays: number = INBOX_UNREAD_RED_DAYS,
): "green" | "amber" | "red" {
  if (oldestDays === null || oldestDays === undefined) return "green";
  if (oldestDays >= redDays) return "red";
  return oldestDays >= amberDays ? "amber" : "green";
}

export function deriveAccount(
  account: DerivableAccount,
  myUserId?: string | null,
): AccountDerived {
  const projects = account.projects ?? [];

  let overdueCount = 0;
  let stalledCount = 0;
  let reviewsForMe = 0;
  let worst: number | null = null;
  let sawUnknownIdle = false;

  for (const p of projects) {
    if (p.overdue) overdueCount++;
    if (isCurrentAssignee(p, myUserId)) reviewsForMe++;
    if (p.stalled) {
      stalledCount++;
      if (p.days_idle === null || p.days_idle === undefined) {
        sawUnknownIdle = true;
      } else if (worst === null || p.days_idle > worst) {
        worst = p.days_idle;
      }
    }
  }

  return {
    openCount: projects.length,
    overdueCount,
    stalledCount,
    reviewsForMe,
    worstStalledDaysIdle: sawUnknownIdle ? null : worst,
  };
}

/**
 * The one project a health card names on its "Next:" line. A stalled project
 * outranks an overdue one on purpose: overdue at least tells you the deadline,
 * whereas stalled means nobody is moving it, which is the thing the dashboard
 * exists to surface. Ties break on the earliest due date, undated last, and
 * the caller's array is never reordered.
 */
export function pickHeadlineProject<T extends DerivableProject>(
  projects: T[] | null | undefined,
): T | null {
  if (!projects || projects.length === 0) return null;

  const rank = (p: T): number => (p.stalled ? 0 : p.overdue ? 1 : 2);
  const dueKey = (p: T): number => {
    const t = p.due_date ? new Date(p.due_date).getTime() : NaN;
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };

  return [...projects].sort((a, b) => rank(a) - rank(b) || dueKey(a) - dueKey(b))[0] ?? null;
}

/* ── firm lens ─────────────────────────────────────────────────────────────── */

export interface DerivableFirmGroup {
  account_name: string;
  account_manager?: { user_id: string; name: string } | null;
  projects?: Array<{ days_idle?: number | null }> | null;
}

export interface ManagerBucket<G extends DerivableFirmGroup> {
  key: string;
  manager: { user_id: string; name: string } | null;
  /** Flattened, so one manager's clients read as a single worst-first list. */
  rows: { group: G; project: NonNullable<G["projects"]>[number] }[];
}

/**
 * `GET /api/my-book/firm` is keyed by ACCOUNT on purpose (the shape Wade's later
 * boards want), but the firm tab presents a per-ACCOUNT-MANAGER lens: one header
 * per manager with every stalled project of theirs underneath. Roll the account
 * groups up into that.
 *
 * Ordering encodes the triage: clients with NO manager come first (nobody owns
 * them), then the managers with the biggest piles; inside a bucket the worst
 * idle leads, and a null `days_idle` — no activity ever recorded — outranks any
 * number rather than sorting as zero.
 */
export function groupFirmByManager<G extends DerivableFirmGroup>(
  groups: G[] | null | undefined,
): ManagerBucket<G>[] {
  const buckets = new Map<string, ManagerBucket<G>>();

  const byName = [...(groups ?? [])].sort((a, b) => a.account_name.localeCompare(b.account_name));
  for (const group of byName) {
    const key = group.account_manager?.user_id ?? "__none__";
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { key, manager: group.account_manager ?? null, rows: [] };
      buckets.set(key, bucket);
    }
    const projects = (group.projects ?? []) as NonNullable<G["projects"]>[number][];
    for (const project of projects) bucket.rows.push({ group, project });
  }

  for (const bucket of buckets.values()) {
    bucket.rows.sort((a, b) => {
      const av = a.project.days_idle ?? Number.POSITIVE_INFINITY;
      const bv = b.project.days_idle ?? Number.POSITIVE_INFINITY;
      return bv - av || a.group.account_name.localeCompare(b.group.account_name);
    });
  }

  return [...buckets.values()].sort((a, b) => {
    const aNone = a.manager ? 1 : 0;
    const bNone = b.manager ? 1 : 0;
    return (
      aNone - bNone ||
      b.rows.length - a.rows.length ||
      (a.manager?.name ?? "").localeCompare(b.manager?.name ?? "")
    );
  });
}

/** The four summary tiles: totals of PROJECTS (and of waiting-on-client items). */
export function bookTotals(
  accounts: DerivableAccount[],
  myUserId?: string | null,
): BookTotals {
  const totals: BookTotals = { stalled: 0, overdue: 0, reviewsForMe: 0, waitingOnClient: 0 };

  for (const account of accounts) {
    const d = deriveAccount(account, myUserId);
    totals.stalled += d.stalledCount;
    totals.overdue += d.overdueCount;
    totals.reviewsForMe += d.reviewsForMe;
    totals.waitingOnClient += account.relationship?.waiting_on_client?.count ?? 0;
  }

  return totals;
}
